import { ComponentProps } from 'react';

import {
  FINANCE_QUOTE_OPTION_STATUS,
  FinanceQuoteOption,
} from 'src/checkout/components/FinanceQuoteOption/FinanceQuoteOptionLegacy';
import { FINANCE_QUOTE_PROVIDER, FinanceQuote } from 'src/data/FinanceApi/FinanceApi.types';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { getOr } from 'src/utils/getOr';

import { BUY_TYPE, FINANCE_QUOTE_REPAYMENT_FREQUENCY } from './CheckoutForm.type';

type QuoteOption = Prettify<
  (ComponentProps<typeof FinanceQuoteOption> | { provider: FINANCE_QUOTE_PROVIDER.CARMA }) & {
    value: string;
  }
>;

const providersOrder = [
  FINANCE_QUOTE_PROVIDER.ALLIED,
  FINANCE_QUOTE_PROVIDER.PLENTI,
  FINANCE_QUOTE_PROVIDER.WISR,
  FINANCE_QUOTE_PROVIDER.VWFS,
] as const;

const hasQuote = (value: QuoteOption) => getOr(value, 'quote.offer', false);
const getRepaymentValue = (value: QuoteOption) => getOr(value, 'quote.offer.repayment_amount_cents', 0);

export const generateQuoteOptions = (
  quotes: FinanceQuote[],
  loanTerm: number,
  frequency: FINANCE_QUOTE_REPAYMENT_FREQUENCY,
  isPollingQuotes: boolean,
  buyType: BUY_TYPE,
  carPrice: number,
  deposit: number,
): QuoteOption[] => {
  const result: QuoteOption[] = [];
  const dataReturn = { buyType, frequency, carPrice, deposit };

  // Filter the quotes based on the term
  result.push(
    ...providersOrder.map((provider): QuoteOption => {
      // Check if there is a quote
      const quote = quotes.find(
        (quote) =>
          quote.financier === provider && (quote.offer.term_months === loanTerm || quote.offer.term_months === 0),
      );

      if (quote) {
        if (quote.state === 'denied') {
          return {
            ...dataReturn,
            provider,
            value: quote.quote_id,
            status: FINANCE_QUOTE_OPTION_STATUS.DENIED,
            refusal: quote.refusal,
          };
        }

        return {
          ...dataReturn,
          provider,
          value: quote.quote_id,
          status: FINANCE_QUOTE_OPTION_STATUS.AVAILABLE,
          quote,
        };
      }

      return {
        ...dataReturn,
        provider,
        value: provider,
        status: isPollingQuotes ? FINANCE_QUOTE_OPTION_STATUS.LOADING : FINANCE_QUOTE_OPTION_STATUS.UNAVAILABLE,
      };
    }),
  );

  const sortedResult = result
    .filter((value) => hasQuote(value))
    .sort((a, b) => (getRepaymentValue(a) > getRepaymentValue(b) ? 1 : -1))
    .concat(result.filter((value) => !hasQuote(value)));

  // Add the "can't decide option"
  sortedResult.push({ provider: FINANCE_QUOTE_PROVIDER.CARMA, value: FINANCE_QUOTE_PROVIDER.CARMA });

  return sortedResult;
};

export const formatQuoteForPaymentSummary = (
  quote: FinanceQuote | undefined,
  repaymentFrequency: FINANCE_QUOTE_REPAYMENT_FREQUENCY,
) => {
  if (!quote) {
    return '';
  }
  const repaymentMultiplier = repaymentFrequency === FINANCE_QUOTE_REPAYMENT_FREQUENCY.WEEKLY ? 12 / 52 : 1;

  const repayment = formatNumber((quote.offer.repayment_amount_cents / 100) * repaymentMultiplier, {
    prepend: '$',
    decimals: 2,
  });
  const rate = formatNumber(quote.offer.rate * 100, { decimals: 2, append: '%' });
  const frequency = repaymentFrequency === FINANCE_QUOTE_REPAYMENT_FREQUENCY.WEEKLY ? '/wk' : '/month';
  return `${repayment}${frequency} based on sample ${rate} p.a.`;
};
